import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { numify } from 'numify';
import { useTranslation } from 'react-i18next';
/////////////////////////////////////////////////////////////
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { telegramWebApp } from '@/main';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Button,
	Close,
	CoinImage,
	DrawerDescription,
} from '@/app/shared/components';
import {
	Activity,
	ComboCard,
	UpgradeActivityResponse,
} from '@/core/interfaces';
import { ActivitiesService } from '@/core/services';
import {
	setComboCards,
	setCounter,
	setDiamonds,
	setIncrementByHour,
	setIncrementBySecond,
	setPendingBuildAction,
	setShowDrawerDailyComboFound,
	setStickerWonDialog,
	setTotalCoinsGenerated,
	upgradeActivity,
} from '@/core/redux/slices';
import { useScrollBehavior } from '@/app/hooks';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface ActivityDetailProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
	activity: Activity;
}

export const ActivityDetail: React.FC<ActivityDetailProps> = ({
	isOpen,
	onClose,
	activity,
}: ActivityDetailProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();
	useScrollBehavior(isOpen);
	/*********************
	// #region Redux
	**********************/
	const { counter } = useCustomSelector((state) => state.counter);
	const { rewardCollected } = useCustomSelector((state) => state.categories);

	/*********************
	// #region Functions
	**********************/
	const handleUpgradeActivity = async () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		if (counter < activity.userInfo!.nextLevelPrice) {
			toast.warning(t('general.notEnough'));
			return;
		}

		toast.promise(
			ActivitiesService.upgrade({
				id: activity.id,
			}),
			{
				loading: t('general.loading'),
				success: (data: UpgradeActivityResponse) => {
					// Emit event to Google Analytics
					ReactGA.event({
						category: 'activities',
						action: `Upgrade activity`,
						label: `Upgraded activity ${activity.thumbnail.title}`,
					});
					// Update counter
					dispatch(setCounter(data.currentCoins));
					// Update diamonds
					dispatch(setDiamonds(data.currentDiamonds));

					if (data.isAwardWinning) {
						dispatch(setStickerWonDialog(true));
					}

					dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));
					dispatch(setIncrementByHour(data.data.profitPerHour));
					dispatch(
						setIncrementBySecond(data.data.profitPerHour / 3600)
					);
					dispatch(
						upgradeActivity({
							card: data.data.card,
						})
					);

					if (
						data.data.dailyCombo &&
						data.data.dailyCombo.userComboCards.length > 0
					) {
						const comboCards: ComboCard[] = [];

						data.data.dailyCombo.userComboCards.map((combo) => {
							comboCards.push({
								id: combo.id,
								image: combo.imageUrl,
								title: combo.thumbnail.title,
								description: combo.thumbnail.description,
							});
						});

						dispatch(
							setComboCards({
								comboCards: comboCards,
							})
						);
						dispatch(setShowDrawerDailyComboFound(true));

						if (
							data.data.dailyCombo.userComboCards.length === 3 &&
							!rewardCollected
						) {
							dispatch(setPendingBuildAction(true));
						}
					}

					return `${t('categories.buy.successBuy1')} ${
						data.data.card.thumbnail.title
					} ${t('categories.buy.successBuy2')} ${
						data.data.card.userInfo.currentLevel
					}`;
				},
				error: t('categories.buy.errorBuy'),
			}
		);
		onClose(false);
	};

	/*********************
	// #region Template
	**********************/
	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				<div className="mx-auto w-full max-w-sm ">
					<DrawerHeader>
						<DrawerTitle>
							<VisuallyHidden>
								{activity.thumbnail.title}
							</VisuallyHidden>
							<DrawerClose className="right-5 text-right absolute">
								<Close fillcolor="#fff" />
							</DrawerClose>
						</DrawerTitle>
					</DrawerHeader>

					<DrawerDescription>
						<VisuallyHidden>
							{activity.detail.description}
						</VisuallyHidden>
					</DrawerDescription>

					<div className="mx-auto px-8">
						<img
							className="cursor-pointer mx-auto w-[100px] mb-6"
							src={activity.image}
						/>

						<div className="flex flex-col text-center gap-3 mb-8">
							<p className="font-bold text-2xl">
								{activity.thumbnail.title}
							</p>
							{/* comment description */}
							{/* <p className="font-semibold text-sm text-white text-center">
								{activity.detail.description}
							</p> */}
						</div>

						{activity.userInfo &&
							activity.userInfo?.nextLevelReward > 0 && (
								<div className="flex my-4 justify-center">
									<div className="flex gap-1 text-lg font-bold">
										+
										{activity.buyOnlyOnce &&
										activity.userInfo.currentLevel > 0
											? numify(
													activity.userInfo
														.accumulatedRewards
											  )
											: numify(
													activity.userInfo
														.nextLevelReward
											  )}
										<CoinImage />
										{t('categories.activities.perHour')}
									</div>
								</div>
							)}
					</div>

					<DrawerFooter>
						<DrawerClose asChild>
							<Button
								variant="primary"
								onClick={handleUpgradeActivity}
								disabled={
									activity.buyOnlyOnce &&
									activity.userInfo?.currentLevel &&
									activity.userInfo?.currentLevel > 0
										? true
										: false
								}
							>
								{activity.userInfo ? (
									activity.buyOnlyOnce &&
									activity.userInfo.currentLevel &&
									activity.userInfo.currentLevel > 0 ? (
										t('alreadyOwned')
									) : activity.userInfo.nextLevelPrice ? (
										<div className="flex items-center gap-1">
											<CoinImage />
											{numify(
												activity.userInfo.nextLevelPrice
											)}
										</div>
									) : (
										<div className="flex items-center gap-1">
											<CoinImage />
											{activity.initialPrice > 0
												? numify(activity.initialPrice)
												: t('free')}
										</div>
									)
								) : null}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};
